import { EmployeeFilterFragment, TrainingStatus } from 'src/generated/mloop-graphql';

import { AttributeMap } from 'src/store/slices/job-stage-interview-seat';

type RoleAttributeConfig = {
  attributeMap?: AttributeMap;
  shadowAttributeMap?: AttributeMap;
  reverseShadowAttributeMap?: AttributeMap;
};

type InTrainingRole = 'SHADOW' | 'REVERSE_SHADOW';

/** Groups seat attribute rows into a name-id -> tag-value-ids map; undefined when empty. */
export const toRoleAttributeMap = (
  attributes?: ReadonlyArray<{ attributeNameId?: string | null; attributeTagValueId?: string | null }> | null
): AttributeMap | undefined => {
  const result: AttributeMap = {};
  (attributes ?? []).forEach((attribute) => {
    if (!attribute.attributeNameId || !attribute.attributeTagValueId) {
      return;
    }
    if (!result[attribute.attributeNameId]) {
      result[attribute.attributeNameId] = [];
    }
    result[attribute.attributeNameId].push(attribute.attributeTagValueId);
  });
  return Object.keys(result).length > 0 ? result : undefined;
};

/** Resolves the effective in-training attribute map for a seat role. */
export const resolveInTrainingAttributeMap = ({
  role,
  ...config
}: RoleAttributeConfig & {
  role: InTrainingRole;
}): AttributeMap | undefined => (role === 'SHADOW' ? config.shadowAttributeMap : config.reverseShadowAttributeMap);

export const hasSelectedAttributes = (attributeMap?: AttributeMap): boolean =>
  Boolean(
    attributeMap &&
    Object.values(attributeMap).some((attributeValueIds) => attributeValueIds && attributeValueIds.length > 0)
  );

/**
 * Mirrors the backend's EmployeeWithAttributes.hasAttribute semantics: AND across attribute names,
 * OR within a name (the member needs ANY of the selected values for each selected attribute).
 */
export const memberMatchesAttributeMap = (member: EmployeeFilterFragment, attributeMap?: AttributeMap): boolean => {
  if (!hasSelectedAttributes(attributeMap)) {
    return true;
  }

  const memberAttributeIds = new Set((member.employee?.attributes ?? []).map((attribute) => attribute.id));

  return Object.values(attributeMap ?? {}).every(
    (attributeValueIds) =>
      attributeValueIds.length === 0 ||
      attributeValueIds.some((attributeValueId) => memberAttributeIds.has(attributeValueId))
  );
};

/** Filters module members using the trained map and the per-role resolved in-training maps. */
export const filterModuleMembersBySeatAttributes = ({
  interviewModuleMembers,
  ...config
}: RoleAttributeConfig & {
  interviewModuleMembers: EmployeeFilterFragment[];
}): EmployeeFilterFragment[] => {
  const resolvedShadowAttributeMap = resolveInTrainingAttributeMap({ ...config, role: 'SHADOW' });
  const resolvedReverseShadowAttributeMap = resolveInTrainingAttributeMap({ ...config, role: 'REVERSE_SHADOW' });

  return interviewModuleMembers.filter((member) => {
    if (member.status === TrainingStatus.Trained) {
      return memberMatchesAttributeMap(member, config.attributeMap);
    }

    if (member.status === TrainingStatus.Shadow) {
      return memberMatchesAttributeMap(member, resolvedShadowAttributeMap);
    }

    return memberMatchesAttributeMap(member, resolvedReverseShadowAttributeMap);
  });
};

/** Returns true when either in-training role resolves to a non-empty attribute filter. */
export const hasTraineeAttributeFilters = (config: RoleAttributeConfig): boolean =>
  hasSelectedAttributes(resolveInTrainingAttributeMap({ ...config, role: 'SHADOW' })) ||
  hasSelectedAttributes(resolveInTrainingAttributeMap({ ...config, role: 'REVERSE_SHADOW' }));
